import { useState } from 'react';
import { useDebounceFn } from 'ahooks';
import { useCappsRenterPropertyAddressesWebApiHandler_autocomplete } from '@zg-rentals/ts-capps-client-react-query';
import type { RenterAddressAutocompleteWebComponentDto } from '@zg-rentals/ts-capps-client';

export const MIN_AUTOCOMPLETE_ADDRESS_LENGTH = 7;
export const MAX_AUTOCOMPLETE_ADDRESS_LENGTH = 100;
export const MIN_AUTOCOMPLETE_ADDRESS_WORDS = 2;

export type AddressResponse = RenterAddressAutocompleteWebComponentDto;

export function hasMinimumAutocompleteInput(address: string) {
  const normalized = address.replace(/[,;-]+/g, ' ').trim();
  return (
    normalized.length >= MIN_AUTOCOMPLETE_ADDRESS_LENGTH &&
    normalized.length <= MAX_AUTOCOMPLETE_ADDRESS_LENGTH &&
    normalized.split(/\s+/).length >= MIN_AUTOCOMPLETE_ADDRESS_WORDS
  );
}

export function useAddressAutocomplete({ initialAddress = '' }: { initialAddress?: string }) {
  const [partialAddress, setPartialAddress] = useState(initialAddress);
  const [partialAddressDebounced, setPartialAddressDebounced] = useState(initialAddress);
  const { run: setAddressAutocompleteDebounced } = useDebounceFn(setPartialAddressDebounced, { wait: 300 });

  function onSetAddress(address: string) {
    setPartialAddress(address);
    setAddressAutocompleteDebounced(address);
  }

  const trimmedAddress = partialAddressDebounced.trim();
  const queryHook = useCappsRenterPropertyAddressesWebApiHandler_autocomplete(
    { partialAddress: trimmedAddress },
    {
      enabled: hasMinimumAutocompleteInput(trimmedAddress),
      meta: { tags: ['addressAutocomplete'] },
    },
  );

  return [partialAddress, onSetAddress, queryHook] as const;
}
